import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, ChevronRight, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';
import './Modals.css';

export const CustomizationModal = ({ item, isOpen, onClose }) => {
    const { addToCart } = useCart();
    const [selectedOptions, setSelectedOptions] = useState([]);

    if (!item) return null;

    const toggleOption = (opt) => {
        setSelectedOptions(prev =>
            prev.find(o => o.name === opt.name)
                ? prev.filter(o => o.name !== opt.name)
                : [...prev, opt]
        );
    };

    const handleConfirm = () => {
        const customizedItem = {
            ...item,
            id: `${item.id}-${selectedOptions.map(o => o.name).join('-')}`, // Unique ID for variations
            originalId: item.id,
            name: `${item.name}${selectedOptions.length > 0 ? ' (Personnalisé)' : ''}`,
            price: item.price + selectedOptions.reduce((acc, o) => acc + o.price, 0),
            customizations: selectedOptions
        };
        addToCart(customizedItem);
        onClose();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="modal-overlay">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        className="modal-content"
                    >
                        <button onClick={onClose} className="absolute top-6 right-6 p-2 text-gray-400 hover:text-dark">
                            <X size={24} />
                        </button>

                        <div className="modal-header">
                            <h2 className="modal-title">{item.name}</h2>
                            <p className="text-gray-500 italic mt-2">Personnalisez votre plaisir.</p>
                        </div>

                        <div className="space-y-4 mb-10">
                            {item.options?.map((opt, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => toggleOption(opt)}
                                    className={`option-row ${selectedOptions.find(o => o.name === opt.name) ? 'option-row-selected' : ''}`}
                                >
                                    <div className="option-info">
                                        <span className="option-name">{opt.name}</span>
                                        {opt.price > 0 && <span className="option-price">+{opt.price.toFixed(2)}€</span>}
                                    </div>
                                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${selectedOptions.find(o => o.name === opt.name)
                                            ? 'bg-primary border-primary text-white'
                                            : 'border-gray-300'
                                        }`}>
                                        {selectedOptions.find(o => o.name === opt.name) && <Check size={14} />}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <button
                            onClick={handleConfirm}
                            className="checkout-btn flex items-center justify-center gap-2"
                        >
                            Ajouter au panier
                            <span className="opacity-40">|</span>
                            {(item.price + selectedOptions.reduce((acc, o) => acc + o.price, 0)).toFixed(2)}€
                        </button>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export const UpSellModal = ({ isOpen, onClose }) => {
    const { addToCart } = useCart();
    const suggestions = [
        { id: 6, name: "Frites Patate Douce", price: 7.00, icon: "🍟" },
        { id: 10, name: "Boisson Fraîche", price: 3.50, icon: "🥤" }
    ];

    const addSuggestion = (item) => {
        addToCart(item);
        onClose();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="modal-overlay">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 50 }}
                        className="modal-content text-center"
                    >
                        <h2 className="text-3xl font-black mb-4 italic">Un petit plus ?</h2>
                        <p className="text-gray-500 mb-8">Accompagnez votre burger pour une expérience totale.</p>

                        <div className="upsell-grid">
                            {suggestions.map((s, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => addSuggestion(s)}
                                    className="upsell-card cursor-pointer group"
                                >
                                    <div className="upsell-img group-hover:scale-110 transition-transform">{s.icon}</div>
                                    <h4 className="font-bold mb-1">{s.name}</h4>
                                    <p className="text-primary font-black">{s.price.toFixed(2)}€</p>
                                    <button className="mt-4 bg-dark text-white p-2 rounded-full w-full flex items-center justify-center gap-2 text-xs font-bold">
                                        <Plus size={14} /> Ajouter
                                    </button>
                                </div>
                            ))}
                        </div>

                        <button
                            onClick={onClose}
                            className="mt-10 text-gray-400 font-bold hover:text-dark flex items-center justify-center gap-2 w-full"
                        >
                            Non merci, j'ai tout ce qu'il faut <ChevronRight size={18} />
                        </button>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};
